import { supabaseEnvForFramework, updateEnvFile } from '@bolt/supabase-utils/env';
import { atom, computed, type ReadableAtom } from 'nanostores';
import { ChangeQueue } from './change-queue';
import { SupabaseEdgeFunctionsStore } from './edge-functions';
import { SupabaseMigrationsStore } from './migrations';
import { fetchSupabaseIntegration } from '~/lib/.client/api/supabase';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { waitForProjectImport } from '~/lib/projects';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer } from '~/lib/webcontainer';
import { AnalyticsAction, AnalyticsTrackEvent, DatabaseTrigger } from '~/types/analytics';
import type { PotentialSupabaseMigrationProblem } from '~/types/chat';
import type { SupabaseIntegration } from '~/types/supabase';
import { hmrPropertyFactory } from '~/utils/hmr';
import { waitUntil } from '~/utils/nanostore';

export interface BaseConfirmationData {
  id: string;
}

export interface MigrationData {
  filePath: string;
  shortSummary: string;
  longSummary: string;
}

export interface MigrationsData extends BaseConfirmationData {
  type: 'migration';
  migrations: MigrationData[];
}

export interface ConnectData extends BaseConfirmationData {
  type: 'connect-required';
}

export interface MigrationErrorData extends BaseConfirmationData {
  type: 'migration-error';
  changeId: string;
  problems: [PotentialSupabaseMigrationProblem];
}

export type SupabaseChangeData = MigrationsData | ConnectData | MigrationErrorData;

export type SupabaseStatus =
  | 'UNKNOWN'
  | 'CONFIGURED'
  | 'CONNECTING'
  | 'NOT_CONFIGURED'
  | 'NEED_CONFIRMATION'
  | 'REMOVED'
  | 'UNSUPPORTED'
  | 'CONNECTING_TO_EXISTING_DATABASE'
  | 'UNHEALTHY';

export type SupabaseProjectConfigurationSteps = 'initial' | 'create-new' | 'connect-existing' | 'confirm-creation';

const hmr = hmrPropertyFactory('SupabaseStore');

/**
 * Global store holding all the state related to Supabase.
 */
export class SupabaseStore {
  @hmr()
  requiresConfirmation = atom<'idle' | 'pending' | 'accepted' | 'declined'>('idle');

  @hmr()
  showProjectLimitReached = atom<boolean>(false);

  @hmr()
  showProjectNameAlreadyExists = atom<boolean>(false);

  @hmr()
  showSupabaseProjectConfiguration = atom<boolean>(false);

  @hmr()
  isConfigVisible = atom<boolean>(false);

  @hmr()
  supabaseProjectConfigurationStep = atom<SupabaseProjectConfigurationSteps>('initial');

  @hmr()
  supabaseProjectIntegration = atom<SupabaseIntegration | undefined>();

  @hmr()
  supabaseStatus = atom<SupabaseStatus>('UNKNOWN');

  isConfigured = computed(this.supabaseStatus, (status) => status === 'CONFIGURED');
  supabaseProject = computed(this.supabaseProjectIntegration, (integration) => integration?.project);
  connecting = computed(this.supabaseStatus, (status) => status === 'CONNECTING');

  @hmr()

  // initialize with default value to break circular dependency issues
  supported: ReadableAtom<boolean> = atom(false);

  @hmr()
  applyingMigrations = atom(false);

  @hmr()
  #changesQueue = new ChangeQueue();

  @hmr()
  showBoltNativeDatabaseCreatedNotification = atom<boolean>(false);

  // adding the @hmr() annotation on those would be incorrect because `this` changes
  #migrationsStore = new SupabaseMigrationsStore(this, this.#changesQueue);
  #edgefunctionsStore = new SupabaseEdgeFunctionsStore(this, this.#changesQueue);

  async #fetchAndUpdateIntegration(projectId: string) {
    try {
      const integration = await fetchSupabaseIntegration(projectId);

      if (integration && 'notFound' in integration) {
        /**
         * When this call returns 404, it means there is no active connection to a supabase
         * project.
         *
         * At this point, we need to verify if the project already has migration files
         * cause when it does, this is a project that has previously been connected to
         * supabase but we don't know which one. This is an unsupported use case and we
         * should warn the user about this.
         */
        await waitForProjectImport();

        if (this.supabaseStatus.get() === 'UNKNOWN') {
          this.supabaseStatus.set('NOT_CONFIGURED');
        }

        return;
      }

      if (integration && integration.project.status !== 'ACTIVE_HEALTHY') {
        this.supabaseStatus.set('UNHEALTHY');
        return;
      }

      if (integration) {
        this.supabaseProjectIntegration.set(integration);
        this.supabaseStatus.set('CONFIGURED');
      }
    } catch {
      if (this.supabaseStatus.get() === 'UNKNOWN') {
        this.supabaseStatus.set('NOT_CONFIGURED');
      }
    }
  }

  get hasMigrations() {
    return this.#changesQueue.hasMigrations;
  }

  get firstItemInConfirmationQueue() {
    return this.#changesQueue.firstItemInConfirmationQueue;
  }

  readonly promptDisabled = computed(
    [this.supabaseStatus, this.applyingMigrations, this.firstItemInConfirmationQueue],
    (status, applyingMigrations, firstItemInConfirmationQueue) => {
      /**
       * @todo Not sure if it's the best to disable the prompt when there's failed migrations
       * because the user won't be able to prompt. On the other hand, we want the user to fix the
       * migrations using the "Attempt fix" button becasue it will contain more specific information
       * for how to fix the migration. However, it means the user could potentially be blocked
       * from sending anything if they the LLM ends up in an infinite loop and can't figure out
       * how to fix the issue.
       */
      let isPendingItem = false;

      if (firstItemInConfirmationQueue != undefined) {
        if (firstItemInConfirmationQueue.type === 'migration') {
          isPendingItem = true;
        } else if (firstItemInConfirmationQueue.type === 'connect-required') {
          isPendingItem = false; // explicitly dont disable prompt when connecting to supabase for now
        }
      }

      return status !== 'UNSUPPORTED' && (applyingMigrations || isPendingItem);
    },
  );

  init() {
    this.supabaseProjectIntegration.subscribe(async (integration) => {
      if (integration) {
        await writeDotEnvFile(integration);
      }
    });

    this.#migrationsStore.init();
    this.#edgefunctionsStore.init();

    let setupDone = false;

    const unsubscribe = projectStore.projectId.subscribe(async (projectId) => {
      if (projectId && !setupDone) {
        setupDone = true;

        await this.#fetchAndUpdateIntegration(projectId);

        queueMicrotask(() => unsubscribe());
      }
    });

    // initialize the supported computed property after stores are ready
    this.supported = computed([workbenchStore.framework, this.supabaseStatus], (framework, status) => {
      return status !== 'UNSUPPORTED' && (framework === undefined || framework.name !== 'nextjs');
    });
  }

  getEdgeFunctionsLogs(functionSlug: string) {
    return this.#edgefunctionsStore.getEdgeFunctionsLogs(functionSlug);
  }

  deployEdgeFunctions(): Promise<void> {
    return this.#edgefunctionsStore.deployEdgeFunctions();
  }

  getMigrationFilePath(filePath: string) {
    return this.#migrationsStore.getMigrationFilePath(filePath);
  }

  acceptChange(changeId: string) {
    return this.#migrationsStore.acceptChange(changeId);
  }

  discardChanges(changeId: string) {
    return this.#migrationsStore.discardChanges(changeId);
  }

  isDiscarded(changeId: string) {
    return this.#migrationsStore.isDiscarded(changeId);
  }

  openSupabaseProjectConfiguration = () => {
    this.supabaseProjectConfigurationStep.set('initial');
    this.showSupabaseProjectConfiguration.set(true);
  };

  showSupabaseCreateNewProjectConfiguration = () => {
    this.supabaseProjectConfigurationStep.set('create-new');
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.DatabaseModalOpened,
        properties: {
          source: DatabaseTrigger.Chat,
        },
      },
    });
    this.showSupabaseProjectConfiguration.set(true);
  };

  async refetchIntegration() {
    const projectId = projectStore.getProjectId();

    if (!projectId) {
      return;
    }

    await this.#fetchAndUpdateIntegration(projectId);

    this.showBoltNativeDatabaseCreatedNotification.set(true);
  }
}

async function writeDotEnvFile(supbaseIntegration: SupabaseIntegration) {
  const webcontainerInstance = await webcontainer;

  await waitForProjectImport();

  // wait for streaming and all the fixing to be complete
  await waitUntil(workbenchStore.isProcessing, false);

  const framework = workbenchStore.framework.get()?.name;

  let dotEnv = '';

  try {
    dotEnv = await webcontainerInstance.fs.readFile('.env', 'utf-8');
  } catch {
    // noop
  }

  const { apiKey, project } = supbaseIntegration;

  const { apiKeyEnvVar, databaseUrlEnvVar } = supabaseEnvForFramework(framework);

  const environmentVariables = {
    [apiKeyEnvVar]: apiKey,
    [databaseUrlEnvVar]: `https://${project.id}.supabase.co`,
  };

  const updatedEnv = updateEnvFile(dotEnv, environmentVariables);

  await webcontainerInstance.fs.writeFile('.env', updatedEnv, 'utf8');
}

export const supabaseStore = new SupabaseStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { supabaseStore });
}
